import { useDispatch, useSelector } from 'react-redux'
import { useCreateProductLike, useDeleteProductLike } from '@/apis/rest/generated/hooks'
import { useLikeEventTracker } from '@/containers/event-tracker/like.event-tracker'
import { doSetLikeProductList } from '@/stores/reduxData'
import {
  doInitGlobalSnackBarContent,
  doInitGlobalToastContent,
  doSetGlobalSnackBarType,
  doSetGlobalToastContent,
} from '@/stores/reduxUI'
import { RootState } from '@/stores/store'
import { Base64ConverterUtil } from '@/utils/base64-converter.util'
import { ApiErrorUtils } from '@/utils/utilApiErrors'

export const useMutationProductLike = () => {
  const { mutateAsync: mutateAsyncCreateProductLike } = useCreateProductLike()
  const { mutateAsync: mutateAsyncDeleteProductLike } = useDeleteProductLike()
  const dispatch = useDispatch()
  const { trackClickLikeButtonEvent, trackClickUnLikeButtonEvent } = useLikeEventTracker()
  const likedList = useSelector((rootState: RootState) => rootState.reduxDataReducers.likeProductList)

  const createProductLike = async ({ productId, promotionId }: { productId: number; promotionId?: number }) => {
    try {
      const data = await mutateAsyncCreateProductLike({
        data: {
          productId,
          promotionId,
        },
      })
      dispatch(doInitGlobalToastContent([]))
      dispatch(doSetGlobalSnackBarType('liked'))
      const encodedProductId = Base64ConverterUtil.convertNumberIdToBase64ByKey({ key: 'ProductType', id: productId })
      const encodedPromotionId = Base64ConverterUtil.convertNumberIdToBase64ByKey({
        key: 'ProductPromotionType',
        id: promotionId,
      })
      const newLikedProducts = [...likedList, { productId: encodedProductId, promotionId: encodedPromotionId }]

      dispatch(doSetLikeProductList(newLikedProducts))
      return data
    } catch (error) {
      const errorInfo = ApiErrorUtils.getError(error)
      dispatch(
        doSetGlobalToastContent({
          content: `${errorInfo.message}\n오류가 지속된다면 고객센터에 문의해 주세요`,
          duration: 5,
        })
      )
      throw error
    }
  }

  const deleteProductLike = async ({ productId, promotionId }: { productId: number; promotionId?: number }) => {
    try {
      const data = await mutateAsyncDeleteProductLike({
        data: {
          productId,
          promotionId,
        },
      })
      const encodedProductId = Base64ConverterUtil.convertNumberIdToBase64ByKey({ key: 'ProductType', id: productId })
      const newLikedProducts = likedList.filter((item) => item.productId !== encodedProductId)
      dispatch(doSetLikeProductList(newLikedProducts))
      dispatch(doInitGlobalSnackBarContent([]))
      dispatch(
        doSetGlobalToastContent({
          content: '찜한 상품에서 제거되었어요',
          variant: 'withCTA',
        })
      )
      return data
    } catch (error) {
      const errorInfo = ApiErrorUtils.getError(error)
      dispatch(
        doSetGlobalToastContent({
          content: `${errorInfo.message}\n오류가 지속된다면 고객센터에 문의해 주세요`,
          duration: 5,
        })
      )
      throw error
    }
  }

  const toggleProductLike = async ({
    productId,
    promotionId,
    isLike,
    productName,
    price,
    discountRate,
    discountPrice,
    reviewCount,
    reviewScore,
  }: {
    productId: number
    promotionId?: number
    isLike: boolean
    productName: string
    price: number
    discountRate: number
    discountPrice: number
    reviewCount: number
    reviewScore: number
  }) => {
    triggerLikeEventTracker({
      isLiked: isLike,
      id: productId,
      productName: productName,
      price,
      discountRate,
      discountPrice,
      reviewCount,
      reviewScore,
    })
    if (isLike) {
      await createProductLike({ productId, promotionId })
    } else {
      await deleteProductLike({ productId, promotionId })
    }
  }

  const triggerLikeEventTracker = ({
    isLiked,
    id,
    productName,
    price,
    discountRate,
    discountPrice,
    reviewCount,
    reviewScore,
  }: {
    isLiked: boolean
    id: number
    productName: string
    price: number
    discountRate: number
    discountPrice: number
    reviewCount: number
    reviewScore: number
  }) => {
    if (isLiked) {
      trackClickLikeButtonEvent({
        id,
        productName,
        price,
        discountRate,
        discountPrice,
        reviewCount,
        reviewScore,
      })
    } else {
      trackClickUnLikeButtonEvent({
        id,
        productName,
        price,
        discountRate,
        discountPrice,
        reviewCount,
        reviewScore,
      })
    }
  }

  return {
    toggleProductLike,
  }
}
